import React, { useState } from "react";
import { Plus, Trash2, Heart, FlaskConical, Download, Upload, GitCompareArrows, X, Eye, Lock, BarChart3, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useList } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState, StatusBadge, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const isNew = (d) => d.created_at && (Date.now() - new Date(d.created_at).getTime()) < 30 * 864e5;
const favKey = (p) => `favs_${p}`;
export const getFavs = (p) => { try { return JSON.parse(localStorage.getItem(favKey(p)) || "[]"); } catch { return []; } };
export const setFavsLS = (p, v) => localStorage.setItem(favKey(p), JSON.stringify(v));

export default function DevLibrary({ cfg }) {
  const { prefix, title, subtitle, categories, statuses } = cfg;
  const { canWrite } = useAuth();
  const qc = useQueryClient();
  const admin = canWrite("rnd");
  const { data: items, isLoading } = useList(prefix, `/${prefix}`);
  const { data: ov } = useList(`${prefix}-ov`, `/${prefix}/overview`);
  const [detailId, setDetailId] = useState(null);
  const detail = detailId ? (items || []).find((x) => x.id === detailId) : null;
  const [form, setForm] = useState(null);
  const [favs, setFavs] = useState(getFavs(prefix));
  const [cmpMode, setCmpMode] = useState(false);
  const [sel, setSel] = useState([]);
  const [showCmp, setShowCmp] = useState(false);

  const toggleFav = (id) => {
    const next = favs.includes(id) ? favs.filter((x) => x !== id) : [...favs, id];
    setFavs(next); setFavsLS(prefix, next);
  };
  const del = async (id) => { if (!window.confirm("Delete this development?")) return; await api.delete(`/${prefix}/${id}`); qc.invalidateQueries(); toast.success("Deleted"); };
  const setStatus = async (it, status) => { await api.put(`/${prefix}/${it.id}`, { status }); qc.invalidateQueries(); };
  const togglePub = async (it) => { await api.put(`/${prefix}/${it.id}`, { published: !it.published }); qc.invalidateQueries(); toast.success(it.published ? "Unpublished" : "Published"); };
  const toggleConf = async (it) => { await api.put(`/${prefix}/${it.id}`, { confidential: !it.confidential }); qc.invalidateQueries(); toast.success(it.confidential ? "Marked public" : "Marked confidential"); };

  const onCard = (it) => {
    if (!cmpMode) return setDetailId(it.id);
    setSel((s) => s.includes(it.id) ? s.filter((x) => x !== it.id) : (s.length >= 2 ? [s[1], it.id] : [...s, it.id]));
  };
  const selItems = sel.map((id) => (items || []).find((x) => x.id === id)).filter(Boolean);

  if (isLoading) return <Loading />;

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} testid={`${prefix}-header`}>
        <Button variant={cmpMode ? "default" : "outline"} onClick={() => { setCmpMode((m) => !m); setSel([]); }} className="gap-2" data-testid={`${prefix}-compare-toggle`}>
          <GitCompareArrows className="w-4 h-4" /> {cmpMode ? "Exit Compare" : "Compare"}
        </Button>
        {admin && <Button onClick={() => setForm({ name: "", category: "", status: "Research", description: "", specsText: "", images: [], thumbnail: "" })} className="gap-2" data-testid={`${prefix}-add-btn`}><Plus className="w-4 h-4" /> New Development</Button>}
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-6">
        {[["Total", ov?.total], ["This Month", ov?.this_month], ["In Development", ov?.testing], ["Buyer Ready", ov?.buyer_ready], ["Production Ready", ov?.production_ready]].map(([l, v]) => (
          <Card key={l} className="p-4"><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{l}</div><div className="font-num font-bold text-2xl text-primary">{fmt(v || 0)}</div></Card>
        ))}
      </div>

      {cmpMode && <div className="mb-4 text-sm text-muted-foreground bg-secondary/60 border border-border rounded-lg px-4 py-2.5" data-testid={`${prefix}-compare-hint`}>Select up to 2 developments to compare side by side. Selected: <span className="font-semibold text-foreground">{sel.length}/2</span></div>}

      {!items?.length ? <EmptyState title="No developments yet" hint={admin ? "Add your first R&D development." : "Our R&D team will publish new developments here soon."} /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((it) => {
            const thumb = it.thumbnail || it.images?.[0];
            const picked = sel.includes(it.id);
            return (
              <Card key={it.id} className={cn("kpi-card overflow-hidden cursor-pointer group relative", picked && "ring-2 ring-primary")} onClick={() => onCard(it)} data-testid={`${prefix}-card-${it.dev_id}`}>
                {cmpMode && <div className={cn("absolute top-2 left-2 z-20 w-6 h-6 rounded-full border-2 flex items-center justify-center", picked ? "bg-primary border-primary text-primary-foreground" : "bg-black/40 border-white/70 text-transparent")}><Check className="w-3.5 h-3.5" /></div>}
                <div className="relative aspect-[4/3] bg-secondary overflow-hidden">
                  {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /> : <div className="w-full h-full flex items-center justify-center"><FlaskConical className="w-10 h-10 text-muted-foreground" /></div>}
                  {isNew(it) && <span className="absolute top-2 left-2 text-[10px] font-bold bg-primary text-primary-foreground px-2 py-0.5 rounded-full tracking-wide">NEW</span>}
                  {it.confidential && <span className="absolute top-2 right-2 text-[10px] font-bold bg-destructive text-destructive-foreground px-2 py-0.5 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" /> CONFIDENTIAL</span>}
                  {!cmpMode && <button onClick={(e) => { e.stopPropagation(); toggleFav(it.id); }} className="absolute bottom-2 right-2 p-1.5 rounded-full bg-black/40 backdrop-blur" data-testid={`fav-${it.dev_id}`}><Heart className={`w-4 h-4 ${favs.includes(it.id) ? "fill-red-500 text-red-500" : "text-white"}`} /></button>}
                </div>
                <div className="p-3">
                  <div className="text-[10px] font-num text-muted-foreground flex items-center justify-between">{it.dev_id}{admin && !it.published && <span className="text-warning font-semibold">DRAFT</span>}</div>
                  <div className="font-semibold text-sm truncate">{it.name}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-muted-foreground">{it.category}</span>
                    <StatusBadge status={it.status} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {cmpMode && sel.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-card border border-border shadow-2xl rounded-full pl-4 pr-2 py-2 flex items-center gap-3" data-testid={`${prefix}-compare-bar`}>
          <span className="text-sm font-medium">{sel.length} selected</span>
          <Button size="sm" variant="ghost" onClick={() => setSel([])} className="gap-1"><X className="w-4 h-4" /> Clear</Button>
          <Button size="sm" disabled={sel.length !== 2} onClick={() => setShowCmp(true)} className="gap-1 rounded-full" data-testid={`${prefix}-compare-open`}><GitCompareArrows className="w-4 h-4" /> Compare</Button>
        </div>
      )}

      {detail && <DetailDialog cfg={cfg} it={detail} admin={admin} onClose={() => setDetailId(null)} fav={favs.includes(detail.id)} onFav={() => toggleFav(detail.id)} onDelete={del} onStatus={setStatus} onPub={togglePub} onConf={toggleConf} statuses={statuses} />}
      {form && <FormDialog cfg={cfg} form={form} setForm={setForm} categories={categories} statuses={statuses} onClose={() => setForm(null)} onSaved={() => { setForm(null); qc.invalidateQueries(); }} />}
      {showCmp && selItems.length === 2 && <CompareDialog a={selItems[0]} b={selItems[1]} onClose={() => setShowCmp(false)} />}
    </div>
  );
}

function AnalyticsPanel({ cfg, it }) {
  const { data, isLoading } = useList(`${cfg.prefix}-an-${it.id}`, `/${cfg.prefix}/${it.id}/analytics`);
  if (isLoading) return <div className="text-sm text-muted-foreground py-2">Loading analytics…</div>;
  const stat = [["Unique Viewers", data?.unique_viewers], ["Total Views", data?.total_views], ["Favorites", data?.favorites]];
  return (
    <div className="border-t border-border pt-3" data-testid="dev-analytics">
      <div className="flex items-center gap-2 text-sm font-semibold mb-2"><BarChart3 className="w-4 h-4 text-primary" /> Buyer Visibility</div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {stat.map(([l, v]) => <div key={l} className="bg-secondary/60 rounded-lg p-2.5 text-center"><div className="font-num font-bold text-xl text-primary">{fmt(v || 0)}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div></div>)}
      </div>
      {data?.viewers?.length > 0 && (
        <div className="mb-2">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Viewed by</div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {data.viewers.map((v, i) => (
              <div key={i} className="flex items-center justify-between text-xs border-b border-border/50 py-1" data-testid="dev-analytics-viewer">
                <span className="font-medium truncate">{v.name} <span className="text-muted-foreground">· {(v.role || "").replace(/_/g, " ")}</span></span>
                <span className="text-muted-foreground shrink-0 ml-2 flex items-center gap-2"><span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{v.count}</span>{v.last_at ? new Date(v.last_at).toLocaleDateString() : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {data?.favorited_by?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Favorited by</div>
          <div className="flex flex-wrap gap-1.5">
            {data.favorited_by.map((f, i) => <span key={i} className="text-xs bg-red-500/10 text-red-500 rounded-full px-2 py-0.5 flex items-center gap-1"><Heart className="w-3 h-3 fill-red-500" /> {f.name}</span>)}
          </div>
        </div>
      )}
      {!data?.viewers?.length && !data?.favorited_by?.length && <div className="text-xs text-muted-foreground">No buyer activity yet. Publish this development so buyers can view it in their portal.</div>}
    </div>
  );
}

function DetailDialog({ cfg, it, admin, onClose, fav, onFav, onDelete, onStatus, onPub, onConf, statuses }) {
  const [big, setBig] = useState(it.thumbnail || it.images?.[0]);
  const qc = useQueryClient();
  const specs = it.specs || {};
  const addImages = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    const urls = [];
    for (const file of files) { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); urls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`); }
    await api.post(`/${cfg.prefix}/${it.id}/images`, { images: urls }); qc.invalidateQueries(); toast.success("Images added");
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid={`${cfg.prefix}-detail`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap"><span className="font-num text-primary">{it.dev_id}</span> {it.name} <StatusBadge status={it.status} />{it.confidential && <span className="text-[10px] font-bold bg-destructive text-destructive-foreground px-2 py-0.5 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" /> CONFIDENTIAL</span>}</DialogTitle>
          <DialogDescription>{it.category} development details, specs and buyer visibility.</DialogDescription>
        </DialogHeader>
        <div className="aspect-video bg-secondary rounded-lg overflow-hidden flex items-center justify-center">
          {big ? <img src={big} alt="" className="max-h-full max-w-full object-contain" /> : <FlaskConical className="w-12 h-12 text-muted-foreground" />}
        </div>
        {it.images?.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {it.images.map((u, i) => <img key={i} src={u} alt="" onClick={() => setBig(u)} className={`w-16 h-16 rounded object-cover cursor-pointer shrink-0 ${big === u ? "ring-2 ring-primary" : ""}`} />)}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onFav} className="gap-1" data-testid="detail-fav"><Heart className={`w-4 h-4 ${fav ? "fill-red-500 text-red-500" : ""}`} /> {fav ? "Saved" : "Save to Favorites"}</Button>
          {big && <a href={big} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="gap-1"><Download className="w-4 h-4" /> Download Original</Button></a>}
        </div>
        {it.description && <p className="text-sm text-muted-foreground">{it.description}</p>}
        {Object.keys(specs).length > 0 && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm border-t border-border pt-3">
            {Object.entries(specs).map(([k, v]) => <div key={k} className="flex justify-between border-b border-border/50 py-1"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span></div>)}
          </div>
        )}
        {admin && <AnalyticsPanel cfg={cfg} it={it} />}
        {admin && (
          <div className="border-t border-border pt-3 flex flex-wrap items-center gap-2">
            <Select value={it.status} onValueChange={(v) => onStatus(it, v)}><SelectTrigger className="w-44 h-9" data-testid="detail-status"><SelectValue /></SelectTrigger><SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            <Button size="sm" variant={it.published ? "outline" : "default"} onClick={() => onPub(it)} data-testid="detail-publish">{it.published ? "Unpublish" : "Publish"}</Button>
            <Button size="sm" variant={it.confidential ? "destructive" : "outline"} onClick={() => onConf(it)} className="gap-1" data-testid="detail-confidential"><Lock className="w-3.5 h-3.5" /> {it.confidential ? "Confidential" : "Mark Confidential"}</Button>
            <label className="text-sm"><input type="file" accept="image/*" multiple className="hidden" onChange={addImages} id="dev-img" /><Button size="sm" variant="outline" className="gap-1" data-testid="detail-add-photos" onClick={() => document.getElementById("dev-img").click()}><Upload className="w-4 h-4" /> Add Photos</Button></label>
            <Button size="sm" variant="ghost" className="text-destructive gap-1" data-testid="detail-delete" onClick={() => { onDelete(it.id); onClose(); }}><Trash2 className="w-4 h-4" /> Delete</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CompareDialog({ a, b, onClose }) {
  const specKeys = Array.from(new Set([...Object.keys(a.specs || {}), ...Object.keys(b.specs || {})]));
  const col = (it) => {
    const thumb = it.thumbnail || it.images?.[0];
    return (
      <div className="flex-1 min-w-0" data-testid={`compare-col-${it.dev_id}`}>
        <div className="aspect-[4/3] bg-secondary rounded-lg overflow-hidden flex items-center justify-center mb-2">
          {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" /> : <FlaskConical className="w-10 h-10 text-muted-foreground" />}
        </div>
        <div className="text-[10px] font-num text-muted-foreground">{it.dev_id}</div>
        <div className="font-semibold text-sm truncate">{it.name}</div>
        <div className="flex items-center gap-2 mt-1"><span className="text-xs text-muted-foreground">{it.category}</span><StatusBadge status={it.status} /></div>
        {it.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-4">{it.description}</p>}
      </div>
    );
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="compare-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><GitCompareArrows className="w-5 h-5 text-primary" /> Side-by-side Comparison</DialogTitle>
          <DialogDescription>Compare specs and visuals of two developments.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-4">{col(a)}{col(b)}</div>
        {specKeys.length > 0 && (
          <div className="border-t border-border pt-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Technical Specifications</div>
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 text-sm">
              <div className="font-semibold text-xs uppercase text-muted-foreground pb-1">Spec</div>
              <div className="font-semibold text-xs truncate pb-1">{a.name}</div>
              <div className="font-semibold text-xs truncate pb-1">{b.name}</div>
              {specKeys.map((k) => {
                const va = (a.specs || {})[k], vb = (b.specs || {})[k];
                const diff = (va || "") !== (vb || "");
                return (
                  <React.Fragment key={k}>
                    <div className="text-muted-foreground py-1.5 border-t border-border/50">{k}</div>
                    <div className={cn("py-1.5 border-t border-border/50 font-medium", diff && "text-primary")}>{va || "—"}</div>
                    <div className={cn("py-1.5 border-t border-border/50 font-medium", diff && "text-primary")}>{vb || "—"}</div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormDialog({ cfg, form, setForm, categories, statuses, onClose, onSaved }) {
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);
  const upThumb = async (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("thumbnail", `${process.env.REACT_APP_BACKEND_URL}${data.url}`); toast.success("Thumbnail set"); };
  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    const specs = {};
    (form.specsText || "").split("\n").forEach((l) => { const i = l.indexOf(":"); if (i > 0) specs[l.slice(0, i).trim()] = l.slice(i + 1).trim(); });
    setBusy(true);
    try {
      await api.post(`/${cfg.prefix}`, { name: form.name, category: form.category, status: form.status, description: form.description, thumbnail: form.thumbnail, specs, confidential: !!form.confidential });
      toast.success("Development created"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid={`${cfg.prefix}-form`}>
        <DialogHeader><DialogTitle>New {cfg.title} Development</DialogTitle>
          <DialogDescription>Create a new R&D development record.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="dev-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label><Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="dev-category"><SelectValue placeholder="Category" /></SelectTrigger><SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Status</Label><Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label>Thumbnail</Label><input type="file" accept="image/*" className="hidden" id="thumb-up" onChange={upThumb} /><div className="flex items-center gap-2"><Button type="button" size="sm" variant="outline" className="gap-1" onClick={() => document.getElementById("thumb-up").click()}><Upload className="w-3.5 h-3.5" /> Upload</Button>{form.thumbnail && <img src={form.thumbnail} alt="" className="w-10 h-10 rounded object-cover" />}</div></div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div><Label>Technical Specs (one per line, "Key: Value")</Label><Textarea rows={5} value={form.specsText} onChange={(e) => set("specsText", e.target.value)} placeholder={"GSM: 240\nWash durability: Excellent"} data-testid="dev-specs" /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={!!form.confidential} onChange={(e) => set("confidential", e.target.checked)} data-testid="dev-confidential" /> <Lock className="w-3.5 h-3.5" /> Mark as confidential (watermarked for buyers)</label>
          <p className="text-xs text-muted-foreground">Add more photos after creating, from the development's detail view.</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="dev-save">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
